import {
    Component,
    OnInit,
    HostListener,
    Input,
    Output,
    EventEmitter,
    OnChanges,
    OnDestroy,
    SimpleChanges,
    AfterViewInit,
    ElementRef,
    ViewChild,
    ChangeDetectorRef
} from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ConnectedPosition } from '@angular/cdk/overlay';

import {
    ResourceEmployee,
    PlanningAssignment,
    ResourceTimelineDay,
    ResourceTimelineBar,
    ResourceKnowledge,
    ResourceCategory,
    ResourceCustomer,
    ResourcePlant,
    ResourceExternalEvent,
} from '../../../Models/resource.models';

import {
    ResourceCapacityService
} from '../../../Services/resource-capacity.service';

import { ResourceApiService } from '../../../Services/resource-api.service';
import { ResourceAbsenceApiService } from '../../../Services/resource-modules-api.service';
import { ResourceAbsencePlanning } from '../../../Models/resource-modules.models';
import { ResourceAccessService, ResourcePlanningAccess } from '../../../Services/resource-access.service';
import {
    ResourceHiddenExternalEventsDialogComponent,
    ResourceHiddenExternalEventsDialogData,
    ResourceHiddenExternalEventsDialogResult
} from './hidden-external-events-dialog/resource-hidden-external-events-dialog.component';

import {
    applyInteraction,
    columnIndexFromX,
    ResourceColumnLayout,
    ResourceDragMode,
    shiftDateKey
} from './resource-interaction';

export type ResourceViewMode =
    'week' | 'month' | 'quarter' | 'year';

/**
 * Zustand der Mausinteraktion mit der Timeline. Genau ein Zustand ist aktiv,
 * ein Mausvorgang führt genau eine Aktion aus (siehe Kommentar im
 * Interaktionsblock der Komponente).
 */
export type ResourceInteraction =
    | { kind: 'idle' }
    | {
        kind: 'pending';
        mode: ResourceDragMode;
        assignment: PlanningAssignment;
        employeeId: string;
        grabColumn: number;
        startX: number;
        startY: number;
    }
    | {
        kind: 'moving' | 'resizing-start' | 'resizing-end';
        assignment: PlanningAssignment;
        sourceEmployeeId: string;
        targetEmployeeId: string;
        grabColumn: number;
        targetColumn: number;
        /** Termin mit den Werten der aktuellen Zeigerposition (Live-Vorschau). */
        preview: PlanningAssignment | null;
    }
    | {
        kind: 'selecting';
        employeeId: string;
        startColumn: number;
        endColumn: number;
    };

interface ResourcePeriodColumn {
    key: string;
    label: string;
    startDate: string;
    endDate: string;
    isToday: boolean;
}
interface ResourceYearDay {
    key: string;
    dayNumber: number;
    weekday: string;
    isWeekend: boolean;
    isToday: boolean;
}

interface ResourceYearMonth {
    monthIndex: number;
    label: string;
    days: ResourceYearDay[];
}

interface ResourceYearBar {
    assignment: PlanningAssignment;
    startDay: number;
    endDay: number;
    lane: number;
    laneCount: number;
    color: string;
    tooltip: string;
    /** Monitor: Mitarbeiter dieses Balkens (Flächenfarbe). */
    employeeId?: string;
    /** Monitor: Abwesenheitskategorie (CountsAsAbsence) – Balken wird schraffiert. */
    absence?: boolean;
}

/** Legende der Jahresansicht im Monitor-Modus. */
interface ResourceMonitorLegend {
    employees: { id: string; name: string; color: string }[];
    /** Kommen normale Termine bzw. Abwesenheiten in der Ansicht vor? */
    hasNormal: boolean;
    hasAbsence: boolean;
    /** Namen der vorkommenden Abwesenheitskategorien (z. B. Urlaub, Krankenstand). */
    absenceCategories: string[];
}

/**
 * Jahresansicht im Monitor-Modus: Maße der Terminbalken. Zentral konfigurierbar;
 * alle Werte in Pixel. Die normale Planung verwendet weiterhin die festen Maße in
 * getYearBarStyle().
 */
export const MONITOR_YEAR_BARS = {
    /** Unter diese Breite soll kein Balken fallen – sonst wird das Jahr auf zwei Seiten verteilt. */
    minWidth: 6,
    /** Breite bei wenigen parallelen Terminen (wie bisher im Monitor). */
    maxWidth: 16,
    /** Gewünschter Abstand zwischen den Spuren; wird bei Platzmangel bis auf 0 verringert. */
    gap: 4,
    /** Abstand zwischen Tagesbeschriftung (Zahl + Wochentag) und erstem Balken. */
    labelGap: 6,
    /** Sicherheitsabstand zum rechten Rand der Monatsspalte. */
    paddingRight: 4,
    /** Breite der Tagesbeschriftung, solange sie noch nicht gemessen wurde (bisheriger Festwert). */
    fallbackLabelWidth: 52,
    /** Monate je Jahresseite, wenn das Jahr aufgeteilt wird. */
    monthsPerPage: 6,
    /** Abstand der Legende zum Rand (CSS left/right: 16px) plus Abstand zum Kalender. */
    legendEdge: 16,
    legendGap: 8
} as const;

/** Breite und Schrittweite der Balken eines Monats bei gegebener Spurenanzahl. */
interface MonitorYearBarLayout {
    width: number;
    step: number;
}

import { ResourceTeamCatalogApiService } from '../../../Services/resource-team-catalog-api.service';
import { RESOURCE_TOOLTIP_POSITIONS } from '../shared/assignment-tooltip/resource-assignment-tooltip.component';
import {
    MONITOR_NEUTRAL_EMPLOYEE_COLOR, assignEmployeeColors
} from './resource-monitor-colors';

@Component({
    selector: 'app-resource-planning',
    templateUrl: './resource-planning.component.html',
    styleUrls: ['../shared/resource-page-toolbar.css', './resource-planning.component.css']
})
export class ResourcePlanningComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {

    @ViewChild('monitorTimelineScroll')
    monitorTimelineScroll?: ElementRef<HTMLElement>;

    private readonly monitorMinRowHeight = 140;

    @Input() monitorMode = false;
    @Input() monitorView: 'week' | 'year' = 'week';

    @Input() monitorWeekPage = 0;

    @Output() monitorWeekPageCountChange = new EventEmitter<number>();

    /** Monitor, Jahresansicht: angezeigte Jahresseite (0 = Jänner–Juni bzw. ganzes Jahr, 1 = Juli–Dezember). */
    @Input() monitorYearPage = 0;

    /** Monitor: 1 = alle zwölf Monate auf einer Seite, 2 = Jahr auf zwei Seiten verteilt. */
    @Output() monitorYearPageCountChange = new EventEmitter<number>();

    // Wird anhand der Bildschirmhöhe berechnet
    monitorEmployeePages: ResourceEmployee[][] = [];

    private lastEmittedPageCount = 0;

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['monitorView'] && this.monitorMode) {
            this.setViewMode(this.monitorView);
        }
        if (changes['monitorYearPage'] && this.monitorMode && !changes['monitorYearPage'].firstChange) {
            // Andere Jahreshälfte: Legende wechselt ggf. die Seite, Balkenbreiten neu messen.
            this.scheduleMonitorLayout();
        }
    }

    ngAfterViewInit(): void {
        if (!this.monitorMode) return;

        this.observeMonitorResize();
        this.scheduleMonitorLayout();
    }

    ngOnDestroy(): void {
        this.destroyed = true;
        this.monitorResizeObserver?.disconnect();
        this.monitorResizeObserver = undefined;
        if (this.monitorLayoutFrame !== undefined) {
            cancelAnimationFrame(this.monitorLayoutFrame);
            this.monitorLayoutFrame = undefined;
        }
    }

    private destroyed = false;

    // ---- Monitor: Layoutberechnung (Wochenseiten, Jahresseiten, Balkenbreite) --------

    private monitorResizeObserver?: ResizeObserver;
    private monitorLayoutFrame?: number;

    /**
     * Misst Größenänderungen der Komponente (Fenster, Vollbild) und der Legende. Die
     * Legende kommt und geht mit der Jahresansicht; ihr Element wird über den
     * ViewChild-Setter beobachtet.
     */
    private observeMonitorResize(): void {
        if (typeof ResizeObserver === 'undefined' || this.monitorResizeObserver) return;
        this.monitorResizeObserver = new ResizeObserver(() => this.scheduleMonitorLayout());
        this.monitorResizeObserver.observe(this.host.nativeElement);
    }

    @ViewChild('monitorLegendCard')
    set monitorLegendCard(ref: ElementRef<HTMLElement> | undefined) {
        const el = ref?.nativeElement;
        if (el === this.observedLegend) return;
        if (this.observedLegend) this.monitorResizeObserver?.unobserve(this.observedLegend);
        this.observedLegend = el;
        if (el) {
            this.monitorResizeObserver?.observe(el);
        } else if (this.monitorMode) {
            // Legende verschwunden (z. B. keine Mitarbeiter): reservierten Platz freigeben.
            this.scheduleMonitorLayout();
        }
    }
    private observedLegend?: HTMLElement;

    /**
     * Einziger Einstieg für alle Monitor-Layoutberechnungen. Bündelt mehrere Auslöser
     * (Daten, Größe, Ansicht) in einen Frame und stößt Change Detection nur an, wenn
     * sich tatsächlich etwas geändert hat – so entstehen keine Mess-/Render-Schleifen.
     */
    private scheduleMonitorLayout(): void {
        if (!this.monitorMode || this.destroyed || this.monitorLayoutFrame !== undefined) return;
        this.monitorLayoutFrame = requestAnimationFrame(() => {
            this.monitorLayoutFrame = undefined;
            if (this.destroyed) return;
            this.calculateMonitorPageSize();
            const changed = this.updateMonitorYearLayout();
            this.cdr.detectChanges();
            if (changed) {
                // Nach geänderter Seitenzahl bzw. Legendenbreite sind die Monatsspalten anders
                // breit: einmal nachmessen (liefert dann unveränderte Werte und endet).
                this.scheduleMonitorLayout();
            }
        });
    }

    private calculateMonitorPageSize(): void {
        if (!this.monitorMode || this.viewMode !== 'week') {
            return;
        }

        const scrollElement = this.monitorTimelineScroll?.nativeElement;

        if (!scrollElement) {
            return;
        }

        const headerHeight =
            scrollElement.querySelector('.timeline-header')
                ?.getBoundingClientRect().height ?? 50;

        const availableHeight = Math.max(
            140,
            scrollElement.clientHeight - headerHeight - 12
        );

        const pages: ResourceEmployee[][] = [];
        let currentPage: ResourceEmployee[] = [];
        let usedHeight = 0;

        for (const employee of this.filteredEmployees) {
            const bars = this.getBars(employee.id);

            const laneCount = bars.length
                ? Math.max(...bars.map(bar => bar.lane)) + 1
                : 0;

            const calendarHeight = Math.max(
                this.monitorMinRowHeight,
                laneCount * 35 + 18
            );

            const skills = this.getEmployeeSkills(employee);

            const skillHeight = Math.max(
                this.monitorMinRowHeight,
                75 + Math.ceil(skills.length / 2) * 25
            );

            const employeeHeight = Math.max(
                calendarHeight,
                skillHeight
            );

            if (
                currentPage.length > 0 &&
                usedHeight + employeeHeight > availableHeight
            ) {
                pages.push(currentPage);
                currentPage = [];
                usedHeight = 0;
            }

            currentPage.push(employee);
            usedHeight += employeeHeight;
        }

        if (currentPage.length > 0) {
            pages.push(currentPage);
        }

        this.monitorEmployeePages = pages;
        this.updateMonitorPageCount();
    }

    private updateMonitorPageCount(): void {
        if (!this.monitorMode) return;

        const count = Math.max(1, this.monitorEmployeePages.length);

        if (count !== this.lastEmittedPageCount) {
            this.lastEmittedPageCount = count;
            this.monitorWeekPageCountChange.emit(count);
        }
    }

    @HostListener('window:resize')
    onMonitorResize(): void {
        this.scheduleMonitorLayout();
    }



    /** Aktualisiert die Monitoransicht und berücksichtigt den aktuellen Tag. */
    refreshMonitorData(): void {
        if (!this.monitorMode) {
            return;
        }

        const heute = new Date();

        // Monitor immer am aktuellen Datum ausrichten.
        this.anchorDate = heute;

        // Kalenderwoche, Jahresansicht und Heute-Markierungen aktualisieren.
        this.buildDays();

        // Aktuelle Termine neu vom Server laden.
        this.loadAssignments(true);
    }

    planningAccess: ResourcePlanningAccess | null = null;
    /** null = Gesamtplanung (nur globale Planer), sonst Teamansicht. */
    selectedTeamId: number | null = null;
    private teamCategoryIds: number[] | null = null;
    private teamSkillIds: number[] | null = null;

    /**
     * Kategorien im Editor: in der Teamansicht der Teamkatalog (ohne eigene
     * Teamkategorien der globale Katalog), in der Gesamtplanung die globalen
     * Kategorien. Die Kategorie eines bestehenden Termins bleibt immer wählbar.
     */
    get editorCategories(): ResourceCategory[] {
        const current = this.editingAssignment?.id ? this.editingAssignment.categoryId : null;
        const useTeamCatalog = this.selectedTeamId !== null && !!this.teamCategoryIds?.length;
        const allowed = new Set(this.teamCategoryIds ?? []);
        return this.categories.filter(c =>
            (useTeamCatalog ? allowed.has(c.id) : !c.ownerTeamId) || c.id === current);
    }

    /** Skills für Filter und Anzeige: in der Teamansicht der Teamkatalog (sonst global). */
    get teamKnowledge(): ResourceKnowledge[] {
        if (this.selectedTeamId === null) return this.knowledge;
        if (!this.teamSkillIds?.length) return this.knowledge.filter(k => !k.ownerTeamId);
        const allowed = new Set(this.teamSkillIds);
        return this.knowledge.filter(k => allowed.has(k.id));
    }

    teamEmployeeIds: string[] | null = null;
    /** Mitglieder des gewählten Teams geladen (Voraussetzung für das Laden der Teamtermine). */
    private teamMembersReady = false;
    /** Mitarbeiter, Kategorien, Kunden usw. geladen. */
    private masterDataLoaded = false;

    /** Teams, die geplant werden dürfen (global: alle aktiven, Teamleitung: geleitete). */
    get selectableTeams() {
        return this.planningAccess?.planningTeams ?? [];
    }

    get isGlobalPlanner(): boolean {
        return !!this.planningAccess?.isGlobalPlanner;
    }

    /** Ausblenden von Outlook-Terminen gilt für alle Planer – nur globale Planer dürfen es. */
    get canHideExternal(): boolean {
        return !this.monitorMode && this.isGlobalPlanner;
    }

    get selectedTeamName(): string {
        return this.selectableTeams.find(t => t.teamId === this.selectedTeamId)?.teamName ?? '';
    }

    get planningEmployees(): ResourceEmployee[] {
        return this.teamEmployeeIds === null ? this.employees :
            this.employees.filter(e => this.teamEmployeeIds!.includes(e.id));
    }

    onTeamSelectionChange(teamId: number | null): void {
        this.selectedTeamId = teamId;
        this.teamCategoryIds = null;
        this.teamSkillIds = null;
        this.selectedKnowledgeIds = [];
        if (teamId !== null) {
            this.teamCatalogApi.get(teamId, 'categories').subscribe({
                next: ids => { if (this.selectedTeamId === teamId) this.teamCategoryIds = ids; },
                error: err => { if (this.selectedTeamId === teamId) this.loadError = 'Teamkategorien konnten nicht geladen werden: ' + this.errorText(err); }
            });
            this.teamCatalogApi.get(teamId, 'skills').subscribe({
                next: ids => { if (this.selectedTeamId === teamId) this.teamSkillIds = ids; },
                error: err => { if (this.selectedTeamId === teamId) this.loadError = 'Teamskills konnten nicht geladen werden: ' + this.errorText(err); }
            });
        }
        this.selectedEmployeeIds = [];
        this.teamEmployeeIds = teamId === null ? null : [];
        this.teamMembersReady = teamId === null;
        this.assignments = [];
        this.externalEvents = [];
        this.externalDisplayAssignments = [];
        this.absenceDisplayAssignments = [];
        this.invalidateBars();
        if (teamId === null) {
            this.loadAssignments(true);
            return;
        }
        this.api.getTeamDetail(teamId).subscribe({
            next: team => {
                if (this.selectedTeamId !== teamId) return;
                this.teamEmployeeIds = team.members.map(m => m.userId);
                this.teamMembersReady = true;
                this.loadAssignments(true);
            },
            error: err => this.loadError = 'Teammitglieder konnten nicht geladen werden: ' + this.errorText(err)
        });
    }

    /**
     * In der Teamansicht dürfen Teamleiter nur Termine ändern, deren Teilnehmer alle
     * zum Team gehören. Termine mit Mitarbeitern anderer Teams sind dort nur sichtbar
     * (gleiche Regel prüft das Backend). Globale Planer dürfen immer bearbeiten.
     */
    isReadOnlyAssignment(assignment: PlanningAssignment): boolean {
        if (this.isAbsenceAssignment(assignment)) return true;
        if (this.isExternalAssignment(assignment) || this.monitorMode) return false;
        if (this.selectedTeamId === null || this.isGlobalPlanner || this.teamEmployeeIds === null) return false;
        const members = new Set(this.teamEmployeeIds);
        return assignment.participants.some(p => !members.has(p.employeeId));
    }

    isAbsenceAssignment(assignment: PlanningAssignment): boolean {
        return assignment.displayKind === 'absence';
    }

    /** Balken ohne Drag/Resize (Outlook-Termine, Abwesenheiten, fremde Teamtermine). */
    isLockedBar(assignment: PlanningAssignment): boolean {
        return this.isExternalAssignment(assignment) || this.isReadOnlyAssignment(assignment);
    }

    private explainReadOnly(assignment: PlanningAssignment): void {
        const message = this.isAbsenceAssignment(assignment)
            ? 'Genehmigte Abwesenheit – Änderungen erfolgen im Bereich „Abwesenheiten“.'
            : 'Dieser Termin betrifft auch Mitarbeiter anderer Teams und kann hier nur angesehen werden.';
        this.snackBar.open(message, 'OK', { duration: 4000 });
    }

    employees: ResourceEmployee[] = [];
    assignments: PlanningAssignment[] = [];
    // Externe Outlook-Termine, noch nicht in die Planung übernommen
    externalEvents: ResourceExternalEvent[] = [];

    // Nur zur Darstellung verwendete Termine.
    // Diese werden niemals direkt als Planungstermine gespeichert.
    private externalDisplayAssignments: PlanningAssignment[] = [];

    /** Genehmigte Abwesenheiten (Urlaubssystem) – nur Anzeige, schreibgeschützt. */
    private absenceDisplayAssignments: PlanningAssignment[] = [];

    /** Anzahl der aktuell ausgeblendeten Outlook-Termine (Anzeige am Toolbar-Button). */
    hiddenExternalCount = 0;

    /** Schlüssel (userId|uid|start) des Outlook-Termins, dessen Ausblendung gerade gespeichert wird. */
    private hidingExternalKey: string | null = null;
    days: ResourceTimelineDay[] = [];

    /** Sichtbarer Zeitraum: Kalenderwoche (Mo–So) oder Kalendermonat. */
    viewMode: ResourceViewMode = 'week';

    periodColumns: ResourcePeriodColumn[] = [];

    yearMonths: ResourceYearMonth[] = [];

    selectedEmployeeIds: string[] = [];
    selectedYearCategoryIds: number[] = [];

    private readonly yearDayHeight = 24;


    get isOverviewMode(): boolean {
        return this.viewMode === 'quarter' || this.viewMode === 'year';
    }

    get columnCount(): number {
        return this.isOverviewMode
            ? this.periodColumns.length
            : this.days.length;
    }

    anchorDate = new Date();

    searchText = '';

    knowledge: ResourceKnowledge[] = [];
    selectedKnowledgeIds: number[] = [];

    categories: ResourceCategory[] = [];

    editorOpen = false;
    editingAssignment: PlanningAssignment | null = null;

    customers: ResourceCustomer[] = [];
    plants: ResourcePlant[] = [];

    loading = false;
    saving = false;
    loadError = '';

    // Bereits geladener Zeitraum (inkl. Puffer), um unnötige API-Aufrufe zu vermeiden.
    private loadedFrom: string | null = null;
    private loadedTo: string | null = null;

    // Balken je Mitarbeiter werden zwischengespeichert. Ohne Cache erzeugte
    // getBars() bei jeder Change Detection neue Objekte, und *ngFor baute die
    // Balken-Elemente ständig neu auf – mitten in einem Klick oder Drag.
    private barsCache = new Map<string, ResourceTimelineBar[]>();



    // Termine werden mit Puffer um den sichtbaren Zeitraum geladen, damit die
    // Überlastungsprüfung im Editor auch längere Termine berücksichtigt. Die
    // Abfrage ist überlappungsbasiert, lange Termine werden daher immer geladen.
    private readonly loadBufferDays = 31;

    activeAssignmentTooltip: number | null = null;
    activeAssignmentTooltipEmployeeId: string | null = null;
    activeYearTooltipMonth: number | null = null;

    /** Bevorzugt oberhalb, sonst unterhalb (gemeinsam mit „Mein Kalender“). */
    readonly assignmentTooltipPositions: ConnectedPosition[] = RESOURCE_TOOLTIP_POSITIONS;

    /** Statusbereich des Termin-Tooltips (Outlook, Abwesenheit, schreibgeschützt). */
    tooltipFooter(assignment: PlanningAssignment): { icon: string; text: string } | null {
        if (this.isExternalAssignment(assignment)) return { icon: 'info_outline', text: 'Noch nicht in die Planung übernommen' };
        if (this.isAbsenceAssignment(assignment)) return { icon: 'beach_access', text: 'Genehmigte Abwesenheit' };
        if (this.isReadOnlyAssignment(assignment)) return { icon: 'lock_outline', text: 'Betrifft auch andere Teams – nur Ansicht' };
        return null;
    }


    readonly yearTooltipPositions: ConnectedPosition[] = [
        // Bevorzugt rechts vom Terminbalken
        {
            originX: 'end',
            originY: 'center',
            overlayX: 'start',
            overlayY: 'center',
            offsetX: 12
        },

        // Falls rechts kein Platz ist: links
        {
            originX: 'start',
            originY: 'center',
            overlayX: 'end',
            overlayY: 'center',
            offsetX: -12
        },

        // Alternativ oberhalb
        {
            originX: 'center',
            originY: 'top',
            overlayX: 'center',
            overlayY: 'bottom',
            offsetY: -10
        },

        // Alternativ unterhalb
        {
            originX: 'center',
            originY: 'bottom',
            overlayX: 'center',
            overlayY: 'top',
            offsetY: 10
        }
    ];


    showAssignmentTooltip(
        assignmentId: number,
        employeeId?: string,
        monthIndex?: number
    ): void {
        if (this.interactionActive) return;

        this.activeAssignmentTooltip = assignmentId;
        this.activeAssignmentTooltipEmployeeId = employeeId ?? null;
        this.activeYearTooltipMonth = monthIndex ?? null;
    }

    hideAssignmentTooltip(): void {
        this.activeAssignmentTooltip = null;
        this.activeAssignmentTooltipEmployeeId = null;
        this.activeYearTooltipMonth = null;
    }

    constructor(
        private api: ResourceApiService,
        private capacityService: ResourceCapacityService,
        private accessService: ResourceAccessService,
        private teamCatalogApi: ResourceTeamCatalogApiService,
        private absenceApi: ResourceAbsenceApiService,
        private snackBar: MatSnackBar,
        private dialog: MatDialog,
        private cdr: ChangeDetectorRef,
        private host: ElementRef<HTMLElement>
    ) { }

    ngOnInit(): void {
        if (this.monitorMode) {
            this.viewMode = this.monitorView;
            this.calculateMonitorPageSize();
        }


        this.buildDays();
        if (!this.monitorMode) {
            this.accessService.getMine().subscribe({
                next: access => {
                    this.planningAccess = access;
                    if (access.isGlobalPlanner) {
                        this.loadHiddenExternalCount();
                    }
                    // Startauswahl einmalig: eigenes Hauptteam bzw. geleitetes Team; „Gesamtplanung“
                    // nur für globale Planer ohne passendes eigenes Team. Teamleiter planen
                    // immer in einem ihrer Teams.
                    const startTeamId = ResourceAccessService.defaultTeamId(
                        access, access.planningTeams.map(t => t.teamId), access.isGlobalPlanner);
                    if (startTeamId !== null) {
                        this.onTeamSelectionChange(startTeamId);
                    } else if (access.isGlobalPlanner) {
                        this.loadAssignments(true);
                    } else {
                        this.loading = false;
                        this.loadError = 'Du leitest aktuell kein aktives Team.';
                    }
                },
                error: err => this.loadError = 'Planungsberechtigungen konnten nicht geladen werden: ' + this.errorText(err)
            });
        }
        this.loadMasterData();
    }

    private loadMasterData(): void {
        this.loading = true;
        this.loadError = '';

        forkJoin({
            employees: this.api.getEmployees(),
            knowledge: this.api.getKnowledge(),
            categories: this.api.getCategories(),
            customers: this.api.getCustomers(),
            plants: this.api.getPlants()
        }).subscribe({
            next: result => {
                this.employees = result.employees;
                this.knowledge = result.knowledge;
                this.categories = result.categories;
                this.customers = result.customers;
                this.plants = result.plants;
                this.masterDataLoaded = true;
                this.loadAssignments(true);
                this.scheduleMonitorLayout();
            },
            error: err => {
                this.loading = false;
                this.loadError = 'Stammdaten konnten nicht geladen werden: ' + this.errorText(err);
            }
        });
    }

    /**
     * Lädt die Termine für den sichtbaren Zeitraum plus Puffer. Ohne force wird
     * nur geladen, wenn der bereits geladene Zeitraum den sichtbaren nicht abdeckt
     * (z. B. beim Wechsel Woche -> Monat innerhalb des Puffers).
     */
    private loadAssignments(force = false): void {
        if (this.days.length === 0) {
            return;
        }

        const first = this.days[0].key;
        const last = this.days[this.days.length - 1].key;

        if (
            !force &&
            this.loadedFrom !== null &&
            this.loadedTo !== null &&
            first >= this.loadedFrom &&
            last <= this.loadedTo
        ) {
            return;
        }

        const from = shiftDateKey(first, -this.loadBufferDays);
        const to = shiftDateKey(last, this.loadBufferDays);

        // Erst laden, wenn Stammdaten, Berechtigungen/Startauswahl und ggf. Teammitglieder
        // vorliegen – so wird genau einmal für die richtige Ansicht geladen (kein
        // „erst Global, dann Hauptteam“).
        if (!this.masterDataLoaded) return;
        if (!this.monitorMode && (!this.planningAccess || (!this.planningAccess.isGlobalPlanner && this.selectedTeamId === null))) return;
        const teamId = this.monitorMode ? null : this.selectedTeamId;
        if (teamId !== null && !this.teamMembersReady) return;
        this.loading = true;

        // Teamansicht: alle Termine mit aktuellen Teammitgliedern (auch historische ohne
        // TeamId), Outlook-Termine und genehmigte Abwesenheiten der Mitglieder.
        // Abwesenheiten sind ergänzend – ein Fehler dort blockiert die Planung nicht.
        const absenceUserIds = teamId !== null ? this.teamEmployeeIds ?? [] : undefined;
        const absences$ = teamId !== null && absenceUserIds!.length === 0
            ? of([] as ResourceAbsencePlanning[])
            : this.absenceApi.getPlanning(`${from}T00:00:00`, `${to}T23:59:59`, absenceUserIds)
                .pipe(catchError(() => of([] as ResourceAbsencePlanning[])));

        forkJoin({
            assignments: teamId !== null
                ? this.api.getTeamAssignments(teamId, from, to)
                : this.api.getAssignments(from, to),
            externalEvents: teamId !== null
                ? this.api.getTeamExternalEvents(teamId, from, to)
                : this.api.getExternalEvents(from, to),
            absences: absences$
        }).subscribe({
            next: result => {
                if (!this.monitorMode && this.selectedTeamId !== teamId) return;
                this.assignments = result.assignments;
                this.externalEvents = result.externalEvents;

                this.externalDisplayAssignments =
                    this.createExternalDisplayAssignments(
                        result.externalEvents
                    );
                this.absenceDisplayAssignments =
                    this.createAbsenceDisplayAssignments(result.absences);

                this.loadedFrom = from;
                this.loadedTo = to;

                this.loading = false;
                this.loadError = '';

                this.invalidateBars();

                if (this.monitorMode) {
                    // Seitenzahl des Jahres sofort mit den neuen Terminen festlegen, damit der
                    // Monitor die richtige Jahresseite einblendet; Feinmessung danach im Frame.
                    this.updateMonitorYearLayout();
                }
                this.scheduleMonitorLayout();
            },
            error: err => {
                this.loading = false;
                this.loadError =
                    'Termine konnten nicht geladen werden: ' +
                    this.errorText(err);
            }
        });
    }

    /**
     * Genehmigte Abwesenheiten als schreibgeschützte Anzeige-Balken. Negative IDs
     * in einem eigenen Bereich, damit sie nie mit Outlook-Anzeigeterminen kollidieren.
     */
    private createAbsenceDisplayAssignments(
        absences: ResourceAbsencePlanning[]
    ): PlanningAssignment[] {
        return absences
            .filter(absence => absence.end > absence.start)
            .map((absence, index) => {
                const startDate = absence.start.substring(0, 10);
                let endDate = absence.end.substring(0, 10);
                let endTime = absence.end.substring(11, 16) || '23:59';

                // Ende um 00:00 bedeutet "bis Ende des Vortags".
                if (endTime === '00:00' && endDate > startDate) {
                    endDate = shiftDateKey(endDate, -1);
                    endTime = '23:59';
                }

                return {
                    id: -1000000 - index,
                    title: absence.title || absence.categoryName || 'Abwesenheit',
                    startDate,
                    endDate,
                    startTime: absence.start.substring(11, 16) || '00:00',
                    endTime,
                    categoryId: absence.categoryId,
                    category: absence.categoryName || 'Abwesenheit',
                    color: absence.color || '#7b1fa2',
                    participants: [{ employeeId: absence.userId, utilizationPercent: 0 }],
                    weekendDates: [],
                    weekendDays: [],
                    customerId: null,
                    plantId: null,
                    location: '',
                    sendOutlookInvitation: false,
                    displayKind: 'absence' as const
                };
            });
    }

    private createExternalDisplayAssignments(
        events: ResourceExternalEvent[]
    ): PlanningAssignment[] {

        return events
            .filter(event => event.end > event.start)
            .map((event, index) => {
                const startDate = event.start.substring(0, 10);
                const startTime = event.start.substring(11, 16) || '00:00';

                // Bei ganztägigen iCalendar-Terminen ist das Enddatum exklusiv.
                const endDate = event.allDay
                    ? shiftDateKey(event.end.substring(0, 10), -1)
                    : event.end.substring(0, 10);

                const endTime = event.allDay
                    ? '23:59'
                    : event.end.substring(11, 16) || '23:59';

                return {
                    id: -(index + 1),
                    title: event.title || 'Outlook-Termin',
                    startDate,
                    endDate,
                    startTime,
                    endTime,

                    categoryId: null,
                    category: 'Outlook',
                    color: '#94a3b8',

                    participants: [{
                        employeeId: event.userId,
                        utilizationPercent: 0
                    }],

                    weekendDates: [],
                    weekendDays: [],

                    customerId: null,
                    plantId: null,
                    location: '',
                    sendOutlookInvitation: false,

                    sourceExternalUid: event.uid,
                    sourceExternalUserId: event.userId,
                    sourceExternalStart: event.start
                };
            });
    }



    get filteredEmployees(): ResourceEmployee[] {
        return this.planningEmployees.filter(employee => {

            // Mitarbeiterfilter:
            // Keine Auswahl bedeutet alle Mitarbeiter.
            const matchesEmployee =
                this.selectedEmployeeIds.length === 0 ||
                this.selectedEmployeeIds.includes(employee.id);

            // Kenntnissefilter wie bisher beibehalten.
            const matchesKnowledge =
                this.viewMode === 'year' ||
                this.selectedKnowledgeIds.length === 0 ||
                this.selectedKnowledgeIds.every(id =>
                    employee.knowledgeIds.includes(id)
                );

            return matchesEmployee && matchesKnowledge;
        });
    }

    get visibleEmployees(): ResourceEmployee[] {
        if (!this.monitorMode || this.viewMode !== 'week') {
            return this.filteredEmployees;
        }

        return this.monitorEmployeePages[this.monitorWeekPage] ?? [];
    }

    getKnowledgeNames(employee: ResourceEmployee): string[] {
        return this.teamKnowledge
            .filter(item => employee.knowledgeIds.includes(item.id))
            .map(item => item.name);
    }

    /** Titel der Navigation: "13.10. – 19.10.2026" (Woche) bzw. "Oktober 2026" (Monat). */
    get periodLabel(): string {
        const year = this.anchorDate.getFullYear();

        if (this.viewMode === 'year') {
            return String(year);
        }

        if (this.viewMode === 'quarter') {
            const quarter = Math.floor(
                this.anchorDate.getMonth() / 3
            ) + 1;

            return `${quarter}. Quartal ${year}`;
        }

        if (this.viewMode === 'month') {
            return this.anchorDate.toLocaleDateString('de-AT', {
                month: 'long',
                year: 'numeric'
            });
        }

        if (!this.days.length) {
            return '';
        }

        const start = this.days[0].date;
        const end = this.days[this.days.length - 1].date;

        const format = (date: Date): string =>
            date.toLocaleDateString('de-AT', {
                day: '2-digit',
                month: '2-digit'
            });

        return `${format(start)} – ${format(end)}.${end.getFullYear()}`;
    }

    get periodNoun(): string {
        switch (this.viewMode) {
            case 'month': return 'Monat';
            case 'quarter': return 'Quartal';
            case 'year': return 'Jahr';
            default: return 'Woche';
        }
    }

    /** Anzahl sichtbarer Tagesspalten (7 in der Woche, 28–31 im Monat). */
    get dayCount(): number {
        return this.days.length;
    }

    get totalEmployees(): number {
        return this.filteredEmployees.length;
    }

    get visibleAssignmentCount(): number {
        const visibleIds = new Set(this.filteredEmployees.map(e => e.id));

        return this.assignments.filter(assignment =>
            assignment.participants.some(p => visibleIds.has(p.employeeId)) &&
            this.days.some(day =>
                day.key >= assignment.startDate &&
                day.key <= assignment.endDate
            )
        ).length;
    }

    previousPeriod(): void {
        if (this.monitorMode) return;
        this.navigate(-1);
    }

    nextPeriod(): void {
        if (this.monitorMode) return;
        this.navigate(1);
    }

    goToToday(): void {
        if (this.monitorMode) return;
        this.anchorDate = new Date();
        this.buildDays();
        this.loadAssignments();
    }
    trackYearDay(
        _index: number,
        day: ResourceYearDay
    ): string {
        return day.key;
    }

    /** Wechsel Woche/Monat; das gewählte Datum (anchorDate) bleibt erhalten. */
    setViewMode(mode: ResourceViewMode): void {
        if (this.viewMode === mode) {
            return;
        }

        this.viewMode = mode;
        this.clearSelection();
        this.buildDays();
        this.loadAssignments();
        this.scheduleMonitorLayout();
    }

    onEmployeeSelectionChange(ids: string[]): void {
        this.selectedEmployeeIds = ids;
    }

    onYearCategorySelectionChange(ids: number[]): void {
        this.selectedYearCategoryIds = ids;
    }

    get employeeFilterLabel(): string {
        if (this.selectedEmployeeIds.length === 0) {
            return 'Alle Mitarbeiter';
        }

        if (this.selectedEmployeeIds.length === 1) {
            const employee = this.employees.find(
                e => e.id === this.selectedEmployeeIds[0]
            );

            return employee?.name ?? '1 Mitarbeiter';
        }

        return `${this.selectedEmployeeIds.length} Mitarbeiter`;
    }

    get yearCategoryFilterLabel(): string {
        if (this.selectedYearCategoryIds.length === 0) {
            return 'Alle Kategorien';
        }

        if (this.selectedYearCategoryIds.length === 1) {
            const category = this.categories.find(
                c => c.id === this.selectedYearCategoryIds[0]
            );

            return category?.name ?? '1 Kategorie';
        }

        return `${this.selectedYearCategoryIds.length} Kategorien`;
    }

    // ---- Jahresansicht: Balken je Monat (gecacht) und sichtbare Monate ---------------

    private yearBarsKey = '';
    private yearBarsSource: PlanningAssignment[] | null = null;
    private yearBarsEmployees: ResourceEmployee[] | null = null;
    private yearBarsCategories: ResourceCategory[] | null = null;
    private readonly yearBarsCache = new Map<number, ResourceYearBar[]>();

    /**
     * Balken eines Monats; das Ergebnis wird je Monat gecacht, bis sich Termine, Jahr,
     * Filter oder Stammdaten ändern. Der Cache dient dem Template (kein Neuaufbau bei
     * jeder Change Detection) und der Monitor-Layoutberechnung, die die Spurenanzahl
     * aller zwölf Monate benötigt.
     */
    getYearBars(month: ResourceYearMonth): ResourceYearBar[] {
        const key = [
            this.anchorDate.getFullYear(),
            this.monitorMode ? 'm' : 'p',
            this.filteredEmployees.map(e => e.id).join(','),
            this.selectedYearCategoryIds.join(',')
        ].join('#');

        if (key !== this.yearBarsKey || this.yearBarsSource !== this.assignments ||
            this.yearBarsEmployees !== this.employees || this.yearBarsCategories !== this.categories) {
            this.yearBarsCache.clear();
            this.yearBarsKey = key;
            this.yearBarsSource = this.assignments;
            this.yearBarsEmployees = this.employees;
            this.yearBarsCategories = this.categories;
        }

        let bars = this.yearBarsCache.get(month.monthIndex);
        if (!bars) {
            bars = this.computeYearBars(month);
            this.yearBarsCache.set(month.monthIndex, bars);
        }
        return bars;
    }

    /** Anzahl der parallelen Terminspuren eines Monats (0 = keine Termine). */
    private yearLaneCount(month: ResourceYearMonth): number {
        const bars = this.getYearBars(month);
        return bars.length ? bars[0].laneCount : 0;
    }

    private visibleYearMonthsSource: ResourceYearMonth[] | null = null;
    private visibleYearMonthsKey = '';
    private visibleYearMonthsCache: ResourceYearMonth[] = [];

    /**
     * Im Template dargestellte Monate: normale Planung immer alle zwölf; Monitor bei
     * aufgeteiltem Jahr nur die Hälfte der aktuellen Jahresseite (Jänner–Juni bzw.
     * Juli–Dezember). Eine ungültige Seitennummer wird auf die letzte Seite begrenzt.
     */
    get visibleYearMonths(): ResourceYearMonth[] {
        if (!this.monitorMode || this.monitorYearPageCount < 2) {
            return this.yearMonths;
        }
        const page = this.activeMonitorYearPage;
        const key = `${page}/${this.monitorYearPageCount}`;
        if (this.visibleYearMonthsSource !== this.yearMonths || this.visibleYearMonthsKey !== key) {
            const perPage = MONITOR_YEAR_BARS.monthsPerPage;
            this.visibleYearMonthsCache = this.yearMonths.slice(page * perPage, (page + 1) * perPage);
            this.visibleYearMonthsSource = this.yearMonths;
            this.visibleYearMonthsKey = key;
        }
        return this.visibleYearMonthsCache;
    }

    /** Tatsächlich angezeigte Jahresseite (auf die vorhandene Seitenzahl begrenzt). */
    private get activeMonitorYearPage(): number {
        return Math.max(0, Math.min(this.monitorYearPage, this.monitorYearPageCount - 1));
    }

    private computeYearBars(month: ResourceYearMonth): ResourceYearBar[] {
        const year = this.anchorDate.getFullYear();

        const firstDay = this.toDateKey(
            new Date(year, month.monthIndex, 1)
        );

        const lastDay = this.toDateKey(
            new Date(year, month.monthIndex + 1, 0)
        );

        // Nur Termine anzeigen, die einem aktuell sichtbaren
        // Mitarbeiter zugeordnet sind.
        const visibleEmployeeIds = new Set(
            this.filteredEmployees.map(employee => employee.id)
        );

        const relevantAssignments = this.assignments
            .filter(assignment =>
                // Termin liegt im aktuellen Monat
                assignment.startDate <= lastDay &&
                assignment.endDate >= firstDay &&

                // Mindestens ein ausgewählter Mitarbeiter ist beteiligt
                assignment.participants.some(participant =>
                    visibleEmployeeIds.has(participant.employeeId)
                ) &&

                // Kategorienfilter
                (
                    this.selectedYearCategoryIds.length === 0 ||
                    (
                        assignment.categoryId !== null &&
                        this.selectedYearCategoryIds.includes(
                            assignment.categoryId
                        )
                    )
                )
            )

        const bars: ResourceYearBar[] = [];
        const occupiedUntil: number[] = [];

        for (const assignment of relevantAssignments) {
            const visibleStart = assignment.startDate > firstDay
                ? assignment.startDate
                : firstDay;

            const visibleEnd = assignment.endDate < lastDay
                ? assignment.endDate
                : lastDay;

            const startDay = Number(visibleStart.substring(8, 10));
            const endDay = Number(visibleEnd.substring(8, 10));

            const participantNames = assignment.participants
                .filter(participant =>
                    visibleEmployeeIds.has(participant.employeeId)
                )
                .map(participant => {
                    const employee = this.employees.find(
                        e => e.id === participant.employeeId
                    );

                    return employee?.name ?? participant.employeeId;
                });

            // Monitor: ein Balken je sichtbarem Mitarbeiter (Fläche = Mitarbeiterfarbe,
            // linker Streifen = Kategoriefarbe). Sonst wie bisher ein Balken je Termin.
            const targets: (string | null)[] = this.monitorMode
                ? assignment.participants
                    .map(p => p.employeeId)
                    .filter(id => visibleEmployeeIds.has(id))
                : [null];

            for (const employeeId of targets.length ? targets : [null]) {
                // Überlappende Termine erhalten unterschiedliche Spuren.
                let lane = occupiedUntil.findIndex(
                    occupiedEnd => occupiedEnd < startDay
                );

                if (lane === -1) {
                    lane = occupiedUntil.length;
                    occupiedUntil.push(endDay);
                } else {
                    occupiedUntil[lane] = endDay;
                }

                bars.push({
                    assignment,
                    startDay,
                    endDay,
                    lane,
                    laneCount: 1,
                    color: this.monitorMode
                        ? this.monitorEmployeeColor(employeeId)
                        : assignment.color || '#2563eb',
                    employeeId: employeeId ?? undefined,
                    absence: this.monitorMode && this.isAbsenceCategory(assignment),
                    tooltip: [
                        assignment.title,
                        participantNames.join(', '),
                        `${assignment.startDate} – ${assignment.endDate}`
                    ].filter(Boolean).join('\n')
                });
            }
        }

        // Für alle Balken eines Monats dieselbe Spaltenanzahl verwenden.
        // Dadurch überschneiden sich die Balken optisch nicht.
        const laneCount = Math.max(1, occupiedUntil.length);

        for (const bar of bars) {
            bar.laneCount = laneCount;
        }

        return bars;
    }

    isExternalAssignment(
        assignment: PlanningAssignment
    ): boolean {
        return assignment.id < 0 &&
            !!assignment.sourceExternalUid;
    }

    openExternalAssignment(
        assignment: PlanningAssignment
    ): void {
        if (this.monitorMode || this.saving) {
            return;
        }

        if (!this.isExternalAssignment(assignment)) {
            return;
        }

        const newAssignment = this.emptyAssignment(
            assignment.startDate,
            assignment.endDate
        );

        this.editingAssignment = {
            ...newAssignment,

            title: assignment.title,
            startTime: assignment.startTime,
            endTime: assignment.endTime,

            // Kategorie muss bei der Übernahme bewusst ausgewählt werden.
            categoryId: null,
            category: '',

            participants: [{
                employeeId: assignment.sourceExternalUserId!,
                utilizationPercent: 100
            }],

            location: assignment.location,

            sourceExternalUid: assignment.sourceExternalUid,
            sourceExternalUserId: assignment.sourceExternalUserId,
            sourceExternalStart: assignment.sourceExternalStart,

            // Keine erneute Outlook-Einladung verschicken.
            sendOutlookInvitation: false
        };

        this.editorOpen = true;
    }

    // ---- Outlook-Termine ausblenden / wieder einblenden ----
    //
    // Ausgeblendete Termine werden serverseitig gespeichert (für alle Planer) und
    // beim Laden der Outlook-Termine bereits vom Backend herausgefiltert. Hier wird
    // der Termin nach erfolgreichem Speichern zusätzlich sofort aus der Anzeige
    // entfernt – bei einem Fehler bleibt er sichtbar.

    private externalEventKey(event: ResourceExternalEvent): string {
        return `${event.userId}|${event.uid}|${event.start}`;
    }

    /** Quell-Termin eines Anzeige-Eintrags (über die stabilen Outlook-Merkmale, nie über die negative ID). */
    private externalEventOf(assignment: PlanningAssignment): ResourceExternalEvent | null {
        if (!this.isExternalAssignment(assignment)) {
            return null;
        }

        return this.externalEvents.find(event =>
            event.userId === assignment.sourceExternalUserId &&
            event.uid === assignment.sourceExternalUid &&
            event.start === assignment.sourceExternalStart
        ) ?? null;
    }

    isHidingExternal(assignment: PlanningAssignment): boolean {
        if (this.hidingExternalKey === null) {
            return false;
        }
        const event = this.externalEventOf(assignment);
        return !!event && this.externalEventKey(event) === this.hidingExternalKey;
    }

    /** Klick auf das Augensymbol im Outlook-Terminbalken. */
    hideExternalAssignment(event: Event, assignment: PlanningAssignment): void {
        // Nur ausblenden – weder Übernahme-Editor noch Drag-and-drop auslösen.
        event.stopPropagation();
        event.preventDefault();

        if (!this.canHideExternal || this.hidingExternalKey !== null) {
            return;
        }

        const source = this.externalEventOf(assignment);
        if (!source) {
            return;
        }

        const key = this.externalEventKey(source);
        this.hidingExternalKey = key;
        this.hideAssignmentTooltip();

        this.api.hideExternalEvent({
            userId: source.userId,
            uid: source.uid,
            start: source.start,
            end: source.end,
            title: source.title,
            allDay: source.allDay
        }).subscribe({
            next: hidden => {
                this.hidingExternalKey = null;
                this.removeExternalEventFromView(key);
                this.hiddenExternalCount += 1;

                const ref = this.snackBar.open(
                    'Outlook-Termin ausgeblendet.',
                    'Rückgängig',
                    { duration: 6000 }
                );
                ref.onAction().subscribe(() => this.unhideExternalEvent(hidden.id));
            },
            error: err => {
                this.hidingExternalKey = null;
                this.snackBar.open(
                    'Ausblenden fehlgeschlagen: ' + this.errorText(err),
                    'OK',
                    { duration: 6000 }
                );
            }
        });
    }

    private removeExternalEventFromView(key: string): void {
        this.externalEvents = this.externalEvents.filter(
            event => this.externalEventKey(event) !== key
        );
        this.externalDisplayAssignments =
            this.createExternalDisplayAssignments(this.externalEvents);
        this.invalidateBars();
    }

    private unhideExternalEvent(id: number): void {
        this.api.unhideExternalEvent(id).subscribe({
            next: () => {
                this.hiddenExternalCount = Math.max(0, this.hiddenExternalCount - 1);
                this.snackBar.open('Outlook-Termin wieder eingeblendet.', undefined, { duration: 2500 });
                this.refreshAssignments();
            },
            error: err => {
                // 404: Bereits von jemand anderem eingeblendet – Anzeige nur aktualisieren.
                if (err?.status === 404) {
                    this.loadHiddenExternalCount();
                    this.refreshAssignments();
                    return;
                }
                this.snackBar.open(
                    'Einblenden fehlgeschlagen: ' + this.errorText(err),
                    'OK',
                    { duration: 6000 }
                );
            }
        });
    }

    private loadHiddenExternalCount(): void {
        this.api.getHiddenExternalEvents().subscribe({
            next: hidden => this.hiddenExternalCount = hidden.length,
            // Der Zähler ist nur eine Anzeige – ein Fehler hier blockiert die Planung nicht.
            error: () => { }
        });
    }

    /** Öffnet die Liste der ausgeblendeten Outlook-Termine (Wiederherstellen einzeln oder gesamt). */
    openHiddenExternalEventsDialog(): void {
        if (this.monitorMode) {
            return;
        }

        const data: ResourceHiddenExternalEventsDialogData = {
            employees: this.employees
        };

        this.dialog.open<
            ResourceHiddenExternalEventsDialogComponent,
            ResourceHiddenExternalEventsDialogData,
            ResourceHiddenExternalEventsDialogResult
        >(ResourceHiddenExternalEventsDialogComponent, {
            data,
            width: '600px',
            maxWidth: '95vw',
            maxHeight: '85vh',
            autoFocus: false,
            disableClose: true
        }).afterClosed().subscribe(result => {
            if (result) {
                this.hiddenExternalCount = result.remainingCount;
            }
            if (result?.changed) {
                // Wiederhergestellte Termine erscheinen erst nach erneutem Laden
                // (in allen Ansichten, auch über Monatsgrenzen hinweg).
                this.refreshAssignments();
            }
        });
    }

    updateYearTooltipPosition(event: MouseEvent): void {
        const bar = event.currentTarget as HTMLElement;
        const tooltip = bar.querySelector(
            '.year-custom-tooltip'
        ) as HTMLElement | null;

        if (!tooltip) return;

        tooltip.classList.remove('tooltip-left');

        const barRect = bar.getBoundingClientRect();
        const tooltipWidth = tooltip.getBoundingClientRect().width;
        const margin = 16;

        if (barRect.right + 12 + tooltipWidth > window.innerWidth - margin) {
            tooltip.classList.add('tooltip-left');
        }
    }

    getYearBarStyle(bar: ResourceYearBar): {
        [key: string]: string
    } {
        // Normale Planung: feste Maße wie bisher. Monitor: Breite und Abstand aus dem
        // gemessenen Platz der Monatsspalte (siehe monitorYearBarLayout); erst dann,
        // wenn gemessen wurde – davor gelten die festen Maße.
        let barWidth = this.monitorMode ? 16 : 12;
        let barStep = barWidth + 4;
        let labelWidth: number = MONITOR_YEAR_BARS.fallbackLabelWidth;

        if (this.monitorMode && this.monitorYearLayout.barArea > 0) {
            const layout = this.monitorYearBarLayout(bar.laneCount, this.monitorYearLayout.barArea);
            barWidth = layout.width;
            barStep = layout.step;
            labelWidth = this.monitorYearLayout.labelWidth;
        }

        const style: { [key: string]: string } = {
            top: `calc(${((bar.startDay - 1) / 31) * 100}% + 3px)`,
            height: `calc(${((bar.endDay - bar.startDay + 1) / 31) * 100}% - 6px)`,

            // Balken direkt nebeneinander, Spur um Spur
            left: `${labelWidth + bar.lane * barStep}px`,
            width: `${barWidth}px`,

            backgroundColor: bar.color
        };
        return style;
    }

    // ---- Monitor-Modus: Balkenbreite und Jahresseiten ---------------------------------

    /** 1 = ganzes Jahr auf einer Seite, 2 = Jänner–Juni / Juli–Dezember. */
    monitorYearPageCount = 1;

    /** Vom Kalender freizuhaltender Platz (Legende + Abstände) auf der Legendenseite, in px. */
    monitorLegendReserve = 0;

    /**
     * Gemessene Maße der Jahresansicht (nur Monitor):
     * labelWidth = Platz der Tagesbeschriftung ab linkem Rand der Monatsspalte,
     * barArea   = für Balken nutzbare Breite einer Monatsspalte (0 = noch nicht gemessen),
     * legendWidth = zuletzt gemessene Breite der Legenden-Card.
     */
    private monitorYearLayout: { labelWidth: number; barArea: number; legendWidth: number } =
        { labelWidth: MONITOR_YEAR_BARS.fallbackLabelWidth, barArea: 0, legendWidth: 0 };
    private monitorYearBarLayoutCache = new Map<string, MonitorYearBarLayout>();

    /**
     * Balkenbreite und Schrittweite für `laneCount` parallele Spuren in `area` Pixel
     * Breite. Reihenfolge: volle Breite (max. maxWidth) mit gewünschtem Abstand; reicht
     * das nicht, erst den Abstand bis 0 verringern, dann die Breite bis minWidth; und
     * nur wenn selbst das nicht genügt, unter minWidth – die Balken bleiben so immer
     * innerhalb der Spalte und überdecken sich nicht. Dieselbe Funktion entscheidet über
     * die Aufteilung des Jahres (siehe updateMonitorYearLayout).
     */
    private monitorYearBarLayout(laneCount: number, area: number): MonitorYearBarLayout {
        const lanes = Math.max(1, laneCount);
        const cacheKey = `${lanes}|${area}`;
        const cached = this.monitorYearBarLayoutCache.get(cacheKey);
        if (cached) return cached;

        const { minWidth, maxWidth, gap } = MONITOR_YEAR_BARS;
        const safeArea = Math.max(0, area);
        let usedGap = lanes > 1 ? gap : 0;
        let width = (safeArea - (lanes - 1) * usedGap) / lanes;

        if (width < minWidth && lanes > 1) {
            // Abstand so weit verkleinern, dass die Mindestbreite gehalten wird (0 … gap).
            usedGap = Math.max(0, Math.min(gap, Math.floor((safeArea - lanes * minWidth) / (lanes - 1))));
            width = (safeArea - (lanes - 1) * usedGap) / lanes;
        }

        // Auf halbe Pixel abrunden, damit Rundung nie über den Rand führt; nie unter 1 px.
        width = Math.max(1, Math.min(maxWidth, Math.floor(width * 2) / 2));

        const layout = { width, step: width + usedGap };
        this.monitorYearBarLayoutCache.set(cacheKey, layout);
        return layout;
    }

    /**
     * Misst die Jahresansicht und legt fest, ob das Jahr auf eine oder zwei Seiten
     * verteilt wird. Liefert true, wenn sich dargestellte Werte geändert haben.
     *
     * Entscheidung: Breite einer Monatsspalte bei zwölf Monaten (Kalenderbreite ohne
     * Legendenbereich) minus Tagesbeschriftung = Platz für Balken. Fällt die Balkenbreite
     * für den Monat mit den meisten parallelen Spuren unter minWidth, werden zwei Seiten
     * verwendet. Die Spurenanzahl wird nur mit vollständig geladenem Jahr neu bewertet;
     * sonst bleibt die bisherige Seitenzahl (Wochenansicht lädt nur einen Ausschnitt).
     */
    private updateMonitorYearLayout(): boolean {
        if (!this.monitorMode) return false;

        const hostEl = this.host.nativeElement;
        const yearVisible = this.viewMode === 'year';
        const calendar = yearVisible ? hostEl.querySelector<HTMLElement>('.year-calendar') : null;
        const legendEl = hostEl.querySelector<HTMLElement>('.monitor-legend-card');
        const { legendEdge, legendGap, labelGap, paddingRight, monthsPerPage } = MONITOR_YEAR_BARS;

        // 1. Legende: Breite messen (bleibt als letzter Messwert erhalten, solange die
        //    Jahresansicht nicht sichtbar ist). Ohne Mitarbeiter gibt es keine Legende.
        if (legendEl) {
            this.monitorYearLayout.legendWidth = Math.ceil(legendEl.getBoundingClientRect().width);
        }
        const legendShown = this.monitorLegend.employees.length > 0;
        const legendReserve = 0;

        // 2. Tagesbeschriftung: rechter Rand des breitesten Wochentags der ersten Woche.
        const firstBody = calendar?.querySelector<HTMLElement>('.year-month-body') ?? null;
        if (firstBody) {
            const bodyLeft = firstBody.getBoundingClientRect().left;
            let labelRight = 0;
            const days = firstBody.querySelectorAll<HTMLElement>('.year-day');
            for (let i = 0; i < Math.min(7, days.length); i++) {
                const weekday = days[i].querySelector<HTMLElement>('.year-day-weekday');
                const target = weekday ?? days[i].querySelector<HTMLElement>('.year-day-number');
                if (target) {
                    labelRight = Math.max(labelRight, target.getBoundingClientRect().right - bodyLeft);
                }
            }
            if (labelRight > 0) {
                this.monitorYearLayout.labelWidth = Math.ceil(labelRight) + labelGap;
            }
        }
        const labelWidth = this.monitorYearLayout.labelWidth;

        // 3. Kalenderbreite bei zwölf Monaten: gemessen, sonst aus der Karte abgeleitet.
        let calendarWidth = calendar?.clientWidth ?? 0;
        if (!calendarWidth) {
            // Wochenansicht: Kalenderbreite = Karte minus Innenabstand des Scrollbereichs
            // (8 px je Seite bzw. Legendenbereich auf der Legendenseite, siehe CSS) und Rahmen.
            const card = hostEl.querySelector<HTMLElement>('.planning-card') ?? hostEl;
            calendarWidth = Math.max(0, card.clientWidth - 8 - Math.max(8, legendReserve) - 2);
        } else if (legendReserve !== this.monitorLegendReserve) {
            // Reservierung ändert sich: Kalender wird entsprechend schmaler/breiter
            // (ohne Legende gilt der normale Innenabstand von 8 px).
            calendarWidth = Math.max(0, calendarWidth + Math.max(8, this.monitorLegendReserve) - Math.max(8, legendReserve));
        }

        // 4. Seitenzahl: nur bei vollständig geladenem Jahr neu bewerten.
        let pageCount = this.monitorYearPageCount;
        if (calendarWidth > 0 && this.isYearDataLoaded()) {
            const maxLanes = Math.max(0, ...this.yearMonths.map(m => this.yearLaneCount(m)));
            const monthWidth12 = Math.floor(calendarWidth / 12) - 1; // 1 px Trennlinie
            const area12 = monthWidth12 - labelWidth - paddingRight;
            const fits = maxLanes <= 1 || this.monitorYearBarLayout(maxLanes, area12).width >= MONITOR_YEAR_BARS.minWidth;
            pageCount = fits ? 1 : Math.ceil(12 / monthsPerPage);
        }

        // 5. Nutzbare Balkenbreite der aktuell dargestellten Monatsspalten.
        //    Solange Seitenzahl und Legendenbereich unverändert sind, gilt die gemessene
        //    Spaltenbreite; sonst wird sie aus der neuen Kalenderbreite abgeleitet und beim
        //    anschließenden Nachmessen bestätigt.
        const monthCount = pageCount > 1 ? monthsPerPage : 12;
        const layoutStable = pageCount === this.monitorYearPageCount && legendReserve === this.monitorLegendReserve;
        const bodyWidth = firstBody && layoutStable
            ? firstBody.clientWidth
            : Math.floor(calendarWidth / monthCount) - 1;
        const barArea = calendarWidth > 0 ? Math.max(0, bodyWidth - labelWidth - paddingRight) : 0;

        let changed = false;
        if (pageCount !== this.monitorYearPageCount) {
            this.monitorYearPageCount = pageCount;
            this.monitorYearPageCountChange.emit(pageCount);
            changed = true;
        }
        if (legendReserve !== this.monitorLegendReserve) {
            this.monitorLegendReserve = legendReserve;
            changed = true;
        }
        if (barArea !== this.monitorYearLayout.barArea) {
            this.monitorYearLayout.barArea = barArea;
            this.monitorYearBarLayoutCache.clear();
            changed = true;
        }
        return changed;
    }

    /** Sind die Termine des gesamten Jahres der Jahresansicht geladen? */
    private isYearDataLoaded(): boolean {
        const year = this.anchorDate.getFullYear();
        return this.loadedFrom !== null && this.loadedTo !== null &&
            this.loadedFrom <= `${year}-01-01` && this.loadedTo >= `${year}-12-31`;
    }

    // ---- Monitor-Modus: Mitarbeiterfarben, Kategoriefarben, Legende ----------------

    private monitorColorSource: ResourceEmployee[] | null = null;
    private monitorColors = new Map<string, string>();

    /**
     * Deterministische Mitarbeiterfarbe (nur Darstellung): berechnet über alle
     * Mitarbeiter der Planung, damit sie sich bei Ansichtswechsel und Neuladen nicht ändert.
     */
    monitorEmployeeColor(employeeId: string | null | undefined): string {
        if (this.monitorColorSource !== this.employees) {
            this.monitorColorSource = this.employees;
            this.monitorColors = assignEmployeeColors(this.employees.map(e => e.id));
        }
        return (employeeId && this.monitorColors.get(employeeId)) || MONITOR_NEUTRAL_EMPLOYEE_COLOR;
    }

    /**
     * Abwesenheit = Kategorie mit „Zählt als Abwesenheit“ (ResourceCategoryDefs.CountsAsAbsence) –
     * dieselbe fachliche Regel wie in Kapazitätsplanung und Urlaubsverwaltung (Urlaub,
     * Krankenstand, Zeitausgleich … sind so markiert). Keine Namenslisten im Code.
     */
    isAbsenceCategory(assignment: PlanningAssignment): boolean {
        return !!this.categories.find(c => c.id === assignment.categoryId)?.countsAsAbsence;
    }

    /**
     * Monitor, Jahresansicht: Seite der Legende, sodass sie den aktuellen Monat nicht
     * verdeckt. Ganzes Jahr auf einer Seite: Jänner–Juni → rechts, Juli–Dezember → links
     * (wie bisher). Aufgeteiltes Jahr: innerhalb der angezeigten Hälfte liegt der aktuelle
     * Monat in der ersten (→ rechts) oder zweiten Hälfte (→ links); liegt er nicht in der
     * angezeigten Jahreshälfte, gilt einheitlich rechts.
     */
    get monitorLegendSide(): 'left' | 'right' {
        const month = new Date().getMonth();
        if (this.monitorYearPageCount < 2) {
            return month < 6 ? 'right' : 'left';
        }
        const perPage = MONITOR_YEAR_BARS.monthsPerPage;
        const firstMonth = this.activeMonitorYearPage * perPage;
        if (month < firstMonth || month >= firstMonth + perPage) {
            return 'right';
        }
        return month - firstMonth < perPage / 2 ? 'right' : 'left';
    }

    private monitorLegendKey = '';
    private monitorLegendSource: PlanningAssignment[] | null = null;
    private monitorLegendCategories: ResourceCategory[] | null = null;
    private monitorLegendCache: ResourceMonitorLegend = { employees: [], hasNormal: false, hasAbsence: false, absenceCategories: [] };

    /** Nur Mitarbeiter und Kategorien, die in der aktuellen Jahresansicht vorkommen. */
    get monitorLegend(): ResourceMonitorLegend {
        const year = this.anchorDate.getFullYear();
        const visible = this.filteredEmployees;
        // Neu berechnen nur bei geänderten Daten (neue Arrays nach jedem Laden), Jahr oder Filter.
        const key = [year, visible.map(e => e.id).join(','), this.selectedYearCategoryIds.join(',')].join('#');
        if (key === this.monitorLegendKey && this.monitorLegendSource === this.assignments &&
            this.monitorLegendCategories === this.categories) return this.monitorLegendCache;
        this.monitorLegendSource = this.assignments;
        this.monitorLegendCategories = this.categories;

        const first = `${year}-01-01`;
        const last = `${year}-12-31`;
        const visibleIds = new Set(visible.map(e => e.id));
        const employeeIds = new Set<string>();
        const categoryIds = new Set<number | null>();
        for (const a of this.assignments) {
            if (a.startDate > last || a.endDate < first) continue;
            if (this.selectedYearCategoryIds.length > 0 &&
                (a.categoryId === null || !this.selectedYearCategoryIds.includes(a.categoryId))) continue;
            const participants = a.participants.filter(p => visibleIds.has(p.employeeId));
            if (!participants.length) continue;
            participants.forEach(p => employeeIds.add(p.employeeId));
            categoryIds.add(this.categories.some(c => c.id === a.categoryId) ? a.categoryId : null);
        }

        this.monitorLegendKey = key;
        const absence = this.categories.filter(c => c.countsAsAbsence && categoryIds.has(c.id));
        this.monitorLegendCache = {
            employees: visible
                .filter(e => employeeIds.has(e.id))
                .map(e => ({ id: e.id, name: e.name, color: this.monitorEmployeeColor(e.id) })),
            hasAbsence: absence.length > 0,
            hasNormal: [...categoryIds].some(id => !this.categories.find(c => c.id === id)?.countsAsAbsence),
            absenceCategories: absence.map(c => c.name)
        };
        return this.monitorLegendCache;
    }

    trackLegend(_index: number, entry: { id: string | number | null }): string {
        return String(entry.id);
    }

    getYearParticipantNames(
        assignment: PlanningAssignment
    ): string {
        return assignment.participants
            .map(participant =>
                this.employees.find(
                    employee => employee.id === participant.employeeId
                )?.name ?? participant.employeeId
            )
            .join(', ');
    }

    trackYearMonth(
        _index: number,
        month: ResourceYearMonth
    ): number {
        return month.monthIndex;
    }

    trackYearBar(
        _index: number,
        bar: ResourceYearBar
    ): string {
        return `${bar.assignment.id}|${bar.employeeId ?? ''}`;
    }

    /** Cache leeren, wenn sich Termine, Zeitraum oder die Drag-Vorschau ändern. */
    private invalidateBars(): void {
        this.barsCache.clear();
        this.yearBarsCache.clear();
    }


    getDayCapacity(employee: ResourceEmployee, date: string) {
        return this.capacityService.getDayCapacity(
            employee,
            date,
            this.capacityAssignments
        );
    }

    getCapacityProgress(employee: ResourceEmployee, date: string): number {
        return Math.max(
            0,
            Math.min(100, this.getDayCapacity(employee, date).utilizationPercent)
        );
    }

    updateCapacityTooltipPosition(event: MouseEvent): void {
        const trigger = event.currentTarget as HTMLElement;
        const tooltip = trigger.querySelector(
            '.capacity-custom-tooltip'
        ) as HTMLElement | null;

        if (!tooltip) return;

        // Position zurücksetzen
        tooltip.classList.remove(
            'tooltip-bottom',
            'tooltip-left',
            'tooltip-right'
        );

        const triggerRect = trigger.getBoundingClientRect();
        const tooltipRect = tooltip.getBoundingClientRect();

        const margin = 12;

        // Vertikale Position
        if (triggerRect.top < tooltipRect.height + margin) {
            tooltip.classList.add('tooltip-bottom');
        }

        // Horizontale Position
        const centerX = triggerRect.left + triggerRect.width / 2;
        const halfWidth = tooltipRect.width / 2;

        if (centerX - halfWidth < margin) {
            tooltip.classList.add('tooltip-left');
        } else if (centerX + halfWidth > window.innerWidth - margin) {
            tooltip.classList.add('tooltip-right');
        }
    }


    /**
     * Termine, wie sie gezeichnet werden: während eines Verschiebens/Resizes
     * ersetzt die Vorschau den gezogenen Termin. Die echten Daten werden erst
     * nach erfolgreicher API-Antwort geändert.
     */
    private get effectiveAssignments(): PlanningAssignment[] {
        const i = this.interaction;

        let planningAssignments = this.assignments;

        if (
            (
                i.kind === 'moving' ||
                i.kind === 'resizing-start' ||
                i.kind === 'resizing-end'
            ) &&
            i.preview
        ) {
            const preview = i.preview;

            planningAssignments = this.assignments.map(a =>
                a.id === preview.id ? preview : a
            );
        }

        return [
            ...planningAssignments,
            ...this.absenceDisplayAssignments,
            ...(
                this.viewMode === 'week' || this.viewMode === 'month'
                    ? this.externalDisplayAssignments
                    : []
            )
        ];
    }

    trackBar(_index: number, bar: ResourceTimelineBar): number {
        return bar.assignment.id;
    }

    getBars(employeeId: string): ResourceTimelineBar[] {
        const cached = this.barsCache.get(employeeId);
        if (cached) {
            return cached;
        }

        const bars = this.computeBars(employeeId);
        this.barsCache.set(employeeId, bars);
        return bars;
    }

    private computeBars(employeeId: string): ResourceTimelineBar[] {
        if (!this.days.length) {
            return [];
        }

        const firstDay = this.days[0].key;
        const lastDay = this.days[this.days.length - 1].key;

        const relevant = this.effectiveAssignments
            .filter(assignment =>
                assignment.startDate <= lastDay &&
                assignment.endDate >= firstDay &&
                assignment.participants.some(
                    participant => participant.employeeId === employeeId
                )
            )
            .sort((a, b) =>
                a.startDate.localeCompare(b.startDate) ||
                a.endDate.localeCompare(b.endDate) ||
                a.id - b.id
            );

        const occupiedUntil: number[] = [];

        return relevant.map(assignment => {
            const participant = assignment.participants.find(
                p => p.employeeId === employeeId
            )!;

            let start: number;
            let end: number;

            if (this.isOverviewMode) {
                start = this.periodColumns.findIndex(
                    column => column.endDate >= assignment.startDate
                );

                const endIndex = this.periodColumns.findIndex(
                    column => column.startDate > assignment.endDate
                );

                start = start === -1 ? 0 : start;

                end = endIndex === -1
                    ? this.periodColumns.length
                    : endIndex;
            } else {
                const startColumn = this.days.findIndex(
                    day => day.key >= assignment.startDate
                );

                const endIndex = this.days.findIndex(
                    day => day.key > assignment.endDate
                );

                start = startColumn === -1 ? 0 : startColumn;

                end = endIndex === -1
                    ? this.days.length
                    : endIndex;
            }

            let lane = occupiedUntil.findIndex(
                occupiedEnd => occupiedEnd <= start
            );

            if (lane === -1) {
                lane = occupiedUntil.length;
                occupiedUntil.push(end);
            } else {
                occupiedUntil[lane] = end;
            }

            return {
                assignment,
                startColumn: start,
                columnSpan: Math.max(1, end - start),
                utilizationPercent: participant.utilizationPercent,
                lane
            };
        });
    }


    getEmployeeLoad(employee: ResourceEmployee): number {
        const summary = this.capacityService.getSummary(
            employee,
            this.days.map(day => day.key),
            this.capacityAssignments
        );

        return Math.round(summary.utilizationPercent);
    }
    getEmployeeOverloadedDays(
        employee: ResourceEmployee
    ): number {
        return this.capacityService.getSummary(
            employee,
            this.days.map(day => day.key),
            this.capacityAssignments
        ).overloadedDays;
    }

    getEmployeePlannedHours(
        employee: ResourceEmployee
    ): number {
        return this.capacityService.getSummary(
            employee,
            this.days.map(day => day.key),
            this.capacityAssignments
        ).plannedHours;
    }

    getEmployeeAvailableHours(
        employee: ResourceEmployee
    ): number {
        return this.capacityService.getSummary(
            employee,
            this.days.map(day => day.key),
            this.capacityAssignments
        ).availableHours;
    }

    getDayLoad(
        employee: ResourceEmployee,
        date: string
    ): number {
        return this.capacityService.getDayCapacity(
            employee,
            date,
            this.capacityAssignments
        ).utilizationPercent;
    }

    getPeriodLoad(
        employee: ResourceEmployee,
        column: ResourcePeriodColumn
    ): number {
        const dates = this.days
            .filter(day =>
                day.key >= column.startDate &&
                day.key <= column.endDate
            )
            .map(day => day.key);

        const summary = this.capacityService.getSummary(
            employee,
            dates,
            this.capacityAssignments
        );

        return Math.round(summary.utilizationPercent);
    }


    getPeriodCapacity(
        employee: ResourceEmployee,
        column: ResourcePeriodColumn
    ) {
        const dates = this.days
            .filter(day =>
                day.key >= column.startDate &&
                day.key <= column.endDate
            )
            .map(day => day.key);

        return this.capacityService.getSummary(
            employee,
            dates,
            this.capacityAssignments
        );
    }

    getPeriodCapacityProgress(
        employee: ResourceEmployee,
        column: ResourcePeriodColumn
    ): number {
        const utilization = this.getPeriodCapacity(
            employee,
            column
        ).utilizationPercent;

        return Math.max(0, Math.min(100, utilization));
    }


    getPeriodTooltip(
        employee: ResourceEmployee,
        column: ResourcePeriodColumn
    ): string {
        const dates = this.days
            .filter(day =>
                day.key >= column.startDate &&
                day.key <= column.endDate
            )
            .map(day => day.key);

        const summary = this.capacityService.getSummary(
            employee,
            dates,
            this.capacityAssignments
        );

        return [
            `${column.label}: ${column.startDate} bis ${column.endDate}`,
            `Geplant: ${summary.plannedHours} h`,
            `Kapazität: ${summary.availableHours} h`,
            `Auslastung: ${Math.round(summary.utilizationPercent)} %`,
            `Überlastete Tage: ${summary.overloadedDays}`
        ].join('\n');
    }

    isPeriodOverloaded(
        employee: ResourceEmployee,
        column: ResourcePeriodColumn
    ): boolean {
        return this.days.some(day =>
            day.key >= column.startDate &&
            day.key <= column.endDate &&
            this.getDayLoad(employee, day.key) > 100
        );
    }

    getDayCapacityTooltip(
        employee: ResourceEmployee,
        date: string
    ): string {
        const capacity = this.capacityService.getDayCapacity(
            employee,
            date,
            this.capacityAssignments
        );

        const formattedDate = new Date(
            date + 'T12:00:00'
        ).toLocaleDateString('de-AT', {
            weekday: 'long',
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });

        const hours = (value: number): string =>
            value.toLocaleString('de-AT', {
                maximumFractionDigits: 2
            });

        return [
            formattedDate,
            `Geplant: ${hours(capacity.plannedHours)} Stunden`,
            `Kapazität: ${hours(capacity.availableHours)} Stunden`,
            `Auslastung: ${Math.round(capacity.utilizationPercent)} %`,
            capacity.overloaded ? 'Überlastet' : 'Keine Überlastung'
        ].join('\n');
    }

    get averageLoad(): number {
        const employees = this.filteredEmployees;

        if (!employees.length) {
            return 0;
        }

        const total = employees.reduce(
            (sum, employee) => sum + this.getEmployeeLoad(employee),
            0
        );

        return Math.round(total / employees.length);
    }

    get overloadedCount(): number {
        return this.filteredEmployees.filter(
            employee => this.getEmployeeOverloadedDays(employee) > 0
        ).length;
    }

    trackEmployee(_index: number, employee: ResourceEmployee): string {
        return employee.id;
    }

    trackDay(_index: number, day: ResourceTimelineDay): string {
        return day.key;
    }

    trackCategory(_index: number, category: ResourceCategory): number {
        return category.id;
    }

    /** Eine Woche bzw. einen Monat vor (+1) oder zurück (-1). */
    private navigate(direction: -1 | 1): void {
        const current = this.anchorDate;
        const year = current.getFullYear();
        const month = current.getMonth();

        if (this.viewMode === 'year') {
            this.anchorDate = new Date(
                year + direction,
                month,
                Math.min(
                    current.getDate(),
                    new Date(year + direction, month + 1, 0).getDate()
                )
            );
        } else if (this.viewMode === 'quarter') {
            const quarterStart = Math.floor(month / 3) * 3;
            this.anchorDate = new Date(
                year,
                quarterStart + direction * 3,
                1
            );
        } else if (this.viewMode === 'month') {
            const targetMonth = month + direction;
            const daysInTarget = new Date(
                year, targetMonth + 1, 0
            ).getDate();

            this.anchorDate = new Date(
                year,
                targetMonth,
                Math.min(current.getDate(), daysInTarget)
            );
        } else {
            const next = new Date(current);
            next.setDate(next.getDate() + direction * 7);
            this.anchorDate = next;
        }

        this.clearSelection();
        this.buildDays();
        this.loadAssignments();
    }

    /** Sichtbare Tage je nach Ansicht: Mo–So der Woche oder alle Tage des Monats. */
    private buildDays(): void {
        const anchor = new Date(
            this.anchorDate.getFullYear(),
            this.anchorDate.getMonth(),
            this.anchorDate.getDate()
        );

        const year = anchor.getFullYear();
        const month = anchor.getMonth();

        let first: Date;
        let last: Date;

        switch (this.viewMode) {
            case 'year':
                first = new Date(year, 0, 1);
                last = new Date(year, 11, 31);
                break;

            case 'quarter': {
                const quarterStart = Math.floor(month / 3) * 3;
                first = new Date(year, quarterStart, 1);
                last = new Date(year, quarterStart + 3, 0);
                break;
            }

            case 'month':
                first = new Date(year, month, 1);
                last = new Date(year, month + 1, 0);
                break;

            default:
                first = new Date(anchor);
                first.setDate(
                    first.getDate() - ((first.getDay() + 6) % 7)
                );

                last = new Date(first);
                last.setDate(last.getDate() + 6);
                break;
        }

        const todayKey = this.toDateKey(new Date());

        const length = Math.round(
            (Date.UTC(
                last.getFullYear(),
                last.getMonth(),
                last.getDate()
            ) - Date.UTC(
                first.getFullYear(),
                first.getMonth(),
                first.getDate()
            )) / 86400000
        ) + 1;

        this.days = Array.from({ length }, (_, index) => {
            const date = new Date(first);
            date.setDate(first.getDate() + index);

            const key = this.toDateKey(date);
            const weekday = date.getDay();

            return {
                date,
                key,
                label: date.toLocaleDateString('de-AT', {
                    weekday: 'short'
                }),
                dayNumber: date.getDate(),
                isWeekend: weekday === 0 || weekday === 6,
                isToday: key === todayKey
            };
        });

        this.buildPeriodColumns();
        this.buildYearMonths();
        this.invalidateBars();
    }

    activeCapacityTooltip: string | null = null;

    readonly capacityTooltipPositions: ConnectedPosition[] = [
        // Oberhalb
        {
            originX: 'center',
            originY: 'top',
            overlayX: 'center',
            overlayY: 'bottom',
            offsetY: -10
        },

        // Unterhalb
        {
            originX: 'center',
            originY: 'bottom',
            overlayX: 'center',
            overlayY: 'top',
            offsetY: 10
        },

        // Rechts
        {
            originX: 'end',
            originY: 'center',
            overlayX: 'start',
            overlayY: 'center',
            offsetX: 10
        },

        // Links
        {
            originX: 'start',
            originY: 'center',
            overlayX: 'end',
            overlayY: 'center',
            offsetX: -10
        }
    ];

    showCapacityTooltip(employeeId: string, dayKey: string): void {
        this.activeCapacityTooltip = `${employeeId}_${dayKey}`;
    }

    hideCapacityTooltip(): void {
        this.activeCapacityTooltip = null;
    }

    private buildYearMonths(): void {
        this.yearMonths = [];

        const year = this.anchorDate.getFullYear();
        const todayKey = this.toDateKey(new Date());

        for (let month = 0; month < 12; month++) {
            const first = new Date(year, month, 1);
            const numberOfDays = new Date(year, month + 1, 0).getDate();

            const days: ResourceYearDay[] = [];

            for (let day = 1; day <= numberOfDays; day++) {
                const date = new Date(year, month, day);
                const weekday = date.getDay();
                const key = this.toDateKey(date);

                days.push({
                    key,
                    dayNumber: day,
                    weekday: date.toLocaleDateString('de-AT', {
                        weekday: 'short'
                    }),
                    isWeekend: weekday === 0 || weekday === 6,
                    isToday: key === todayKey
                });
            }

            this.yearMonths.push({
                monthIndex: month,
                label: first.toLocaleDateString('de-AT', {
                    month: 'long'
                }),
                days
            });
        }
    }

    private buildPeriodColumns(): void {
        this.periodColumns = [];

        if (!this.isOverviewMode || !this.days.length) {
            return;
        }

        const first = this.days[0].date;
        const last = this.days[this.days.length - 1].date;
        const todayKey = this.toDateKey(new Date());

        if (this.viewMode === 'year') {
            const year = first.getFullYear();

            for (let month = 0; month < 12; month++) {
                const start = new Date(year, month, 1);
                const end = new Date(year, month + 1, 0);

                const startKey = this.toDateKey(start);
                const endKey = this.toDateKey(end);

                this.periodColumns.push({
                    key: startKey,
                    label: start.toLocaleDateString('de-AT', {
                        month: 'short'
                    }),
                    startDate: startKey,
                    endDate: endKey,
                    isToday:
                        todayKey >= startKey &&
                        todayKey <= endKey
                });
            }

            return;
        }

        // Quartalsansicht: Kalenderwochen Montag bis Sonntag.
        // Erste und letzte Woche werden an der Quartalsgrenze
        // abgeschnitten.
        const current = new Date(first);

        while (current <= last) {
            const start = new Date(current);
            const end = new Date(current);

            const daysUntilSunday = 6 - (
                (current.getDay() + 6) % 7
            );

            end.setDate(end.getDate() + daysUntilSunday);

            if (end > last) {
                end.setTime(last.getTime());
            }

            const startKey = this.toDateKey(start);
            const endKey = this.toDateKey(end);

            // ISO-Kalenderwoche über den Donnerstag bestimmen
            const thursday = new Date(start);
            thursday.setDate(
                thursday.getDate() +
                (3 - ((thursday.getDay() + 6) % 7))
            );

            const isoYear = thursday.getFullYear();
            const firstThursday = new Date(isoYear, 0, 4);
            firstThursday.setDate(
                firstThursday.getDate() -
                ((firstThursday.getDay() + 6) % 7) + 3
            );

            const week = 1 + Math.round(
                (Date.UTC(
                    thursday.getFullYear(),
                    thursday.getMonth(),
                    thursday.getDate()
                ) - Date.UTC(
                    firstThursday.getFullYear(),
                    firstThursday.getMonth(),
                    firstThursday.getDate()
                )) / (7 * 86400000)
            );

            this.periodColumns.push({
                key: startKey,
                label: `KW ${week}`,
                startDate: startKey,
                endDate: endKey,
                isToday:
                    todayKey >= startKey &&
                    todayKey <= endKey
            });

            current.setDate(
                current.getDate() + daysUntilSunday + 1
            );
        }
    }

    private toDateKey(date: Date): string {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');

        return `${year}-${month}-${day}`;
    }


    // =====================================================================
    // Interaktion mit der Timeline (Pointer Events, eigener Zustandsautomat)
    //
    // Ein Mausvorgang führt genau EINE Aktion aus:
    //   idle -> pending (Pointer auf Balken gedrückt)
    //        -> moving / resizing-start / resizing-end (nach Mindestbewegung)
    //        -> idle (pointerup: speichern) | idle (Escape/Abbruch)
    //   pending -> idle + Editor öffnen (pointerup ohne Bewegung = Klick)
    //   idle -> selecting (Pointer auf freier Fläche) -> idle + neuer Termin
    //
    // Kein natives HTML5-Drag-and-Drop mehr: dessen drag*-Events kollidierten
    // mit mousedown/mouseup/click, und ein während des Ziehens neu gerendertes
    // Balken-Element bricht einen nativen Drag sofort ab.
    // =====================================================================

    /** Mindestbewegung in Pixeln, ab der aus einem Klick ein Ziehen wird. */
    private readonly dragThresholdPx = 4;

    interaction: ResourceInteraction = { kind: 'idle' };

    /** Aktive Zeile und deren Element während eines Vorgangs. */
    private activeCalendar: HTMLElement | null = null;

    get interactionActive(): boolean {
        return this.interaction.kind !== 'idle';
    }

    get selectionActive(): boolean {
        return this.interaction.kind === 'selecting';
    }

    get selectionEmployeeId(): string | null {
        return this.interaction.kind === 'selecting'
            ? this.interaction.employeeId
            : null;
    }

    /** Zeile, über der aktuell gezogen bzw. markiert wird (für die Hervorhebung). */
    get activeTargetEmployeeId(): string | null {
        const i = this.interaction;
        if (i.kind === 'moving' || i.kind === 'resizing-start' || i.kind === 'resizing-end') {
            return i.targetEmployeeId;
        }
        if (i.kind === 'selecting') {
            return i.employeeId;
        }
        return null;
    }

    /** Termin, der gerade verschoben oder in der Größe verändert wird. */
    get draggingAssignmentId(): number | null {
        const i = this.interaction;
        return i.kind === 'moving' || i.kind === 'resizing-start' || i.kind === 'resizing-end'
            ? i.assignment.id
            : null;
    }

    /** Spaltenlayout der aktuellen Ansicht (Tage oder Kalenderwochen). */
    private get columnLayout(): ResourceColumnLayout {
        if (this.viewMode === 'quarter') {
            return {
                count: this.periodColumns.length,
                startDates: this.periodColumns.map(c => c.startDate),
                endDates: this.periodColumns.map(c => c.endDate),
                dayStep: 7
            };
        }

        const keys = this.days.map(d => d.key);
        return { count: keys.length, startDates: keys, endDates: keys, dayStep: 1 };
    }

    private columnFromPointer(clientX: number, calendar: HTMLElement): number {
        const rect = calendar.getBoundingClientRect();
        return columnIndexFromX(clientX, rect.left, rect.width, this.columnLayout.count);
    }

    /** Kalenderzeile unter dem Zeiger (auch bei horizontalem Scrollen korrekt). */
    private calendarFromPoint(clientX: number, clientY: number): HTMLElement | null {
        const element = document.elementFromPoint(clientX, clientY) as HTMLElement | null;
        return (element?.closest('.employee-calendar') as HTMLElement | null) ?? null;
    }

    private isInsideTimeline(clientX: number, clientY: number): boolean {
        const element = document.elementFromPoint(clientX, clientY) as HTMLElement | null;
        return !!element?.closest('.timeline-scroll');
    }

    /** Pointer auf einem Terminbalken oder Resize-Griff gedrückt. */
    onBarPointerDown(
        event: PointerEvent,
        assignment: PlanningAssignment,
        employeeId: string,
        mode: ResourceDragMode
    ): void {
        if (this.monitorMode) return;

        if (this.isReadOnlyAssignment(assignment)) {
            event.stopPropagation();
            if (event.button === 0 && mode === 'move') {
                this.explainReadOnly(assignment);
            }
            return;
        }
        if (this.isExternalAssignment(assignment)) {
            event.stopPropagation();

            if (event.button === 0 && mode === 'move') {
                this.openExternalAssignment(assignment);
            }

            return;
        }
        // Kalenderzeile darf keine Auswahl beginnen.
        event.stopPropagation();

        if (event.button !== 0 || this.interaction.kind !== 'idle' || this.saving) {
            return;
        }

        const calendar = (event.currentTarget as HTMLElement).closest('.employee-calendar') as HTMLElement | null;
        if (!calendar) {
            return;
        }

        // Verhindert Textauswahl und den nativen Drag von Browsern.
        event.preventDefault();

        this.activeCalendar = calendar;
        this.interaction = {
            kind: 'pending',
            mode,
            assignment,
            employeeId,
            grabColumn: this.columnFromPointer(event.clientX, calendar),
            startX: event.clientX,
            startY: event.clientY
        };
    }

    /** Pointer auf einer freien Kalenderfläche gedrückt: Zeitraum markieren. */
    onCalendarPointerDown(event: PointerEvent, employeeId: string): void {
        if (this.monitorMode) return;
        if (
            event.button !== 0 ||
            this.interaction.kind !== 'idle' ||
            this.isOverviewMode ||
            this.saving
        ) {
            return;
        }

        const target = event.target as HTMLElement;
        if (target.closest('.assignment-bar')) {
            return;
        }

        const calendar = event.currentTarget as HTMLElement;
        const column = this.columnFromPointer(event.clientX, calendar);

        event.preventDefault();

        this.activeCalendar = calendar;
        this.interaction = {
            kind: 'selecting',
            employeeId,
            startColumn: column,
            endColumn: column
        };
    }

    @HostListener('document:pointermove', ['$event'])
    onDocumentPointerMove(event: PointerEvent): void {
        if (this.monitorMode) return;
        const i = this.interaction;

        if (i.kind === 'idle') {
            return;
        }

        if (i.kind === 'pending') {
            const moved =
                Math.abs(event.clientX - i.startX) >= this.dragThresholdPx ||
                Math.abs(event.clientY - i.startY) >= this.dragThresholdPx;

            if (!moved) {
                return;
            }

            const kind: ResourceInteraction['kind'] =
                i.mode === 'move' ? 'moving'
                    : i.mode === 'resize-start' ? 'resizing-start'
                        : 'resizing-end';

            this.interaction = {
                kind,
                assignment: i.assignment,
                sourceEmployeeId: i.employeeId,
                targetEmployeeId: i.employeeId,
                grabColumn: i.grabColumn,
                targetColumn: i.grabColumn,
                preview: null
            };
            this.updateDragTarget(event);
            return;
        }

        if (i.kind === 'selecting') {
            if (this.activeCalendar) {
                const column = this.columnFromPointer(event.clientX, this.activeCalendar);
                if (column !== i.endColumn) {
                    this.interaction = { ...i, endColumn: column };
                }
            }
            return;
        }

        this.updateDragTarget(event);
    }

    /** Zielspalte und Zielzeile aus der Zeigerposition ableiten, Vorschau neu berechnen. */
    private updateDragTarget(event: PointerEvent): void {
        const i = this.interaction;
        if (i.kind !== 'moving' && i.kind !== 'resizing-start' && i.kind !== 'resizing-end') {
            return;
        }

        // Zeile unter dem Zeiger; außerhalb (z. B. über der Mitarbeiterspalte)
        // bleibt die zuletzt bekannte Zeile aktiv.
        const calendar = this.calendarFromPoint(event.clientX, event.clientY) ?? this.activeCalendar;
        if (!calendar) {
            return;
        }
        this.activeCalendar = calendar;

        const targetColumn = this.columnFromPointer(event.clientX, calendar);
        const rowEmployeeId = calendar.dataset['employeeId'] ?? i.sourceEmployeeId;

        // Mitarbeiterwechsel nur beim Verschieben, nicht beim Verlängern.
        const targetEmployeeId = i.kind === 'moving' ? rowEmployeeId : i.sourceEmployeeId;

        if (targetColumn === i.targetColumn && targetEmployeeId === i.targetEmployeeId) {
            return;
        }

        const mode: ResourceDragMode =
            i.kind === 'moving' ? 'move'
                : i.kind === 'resizing-start' ? 'resize-start'
                    : 'resize-end';

        const preview = applyInteraction(i.assignment, this.columnLayout, {
            mode,
            grabColumn: i.grabColumn,
            targetColumn,
            sourceEmployeeId: i.sourceEmployeeId,
            targetEmployeeId
        });

        this.interaction = { ...i, targetColumn, targetEmployeeId, preview };
        this.invalidateBars();
    }

    @HostListener('document:pointerup', ['$event'])
    onDocumentPointerUp(event: PointerEvent): void {
        if (this.monitorMode) return;
        const i = this.interaction;

        if (i.kind === 'idle') {
            return;
        }

        if (event.button !== 0 && event.pointerType === 'mouse') {
            return;
        }

        // Klick ohne Bewegung: bestehenden Termin öffnen.
        if (i.kind === 'pending') {
            this.resetInteraction();
            this.openAssignment(i.assignment);
            return;
        }

        if (i.kind === 'selecting') {
            const inside = this.isInsideTimeline(event.clientX, event.clientY);
            const employeeId = i.employeeId;
            const first = Math.min(i.startColumn, i.endColumn);
            const last = Math.max(i.startColumn, i.endColumn);
            const layout = this.columnLayout;

            this.resetInteraction();

            if (!inside || !layout.startDates[first] || !layout.endDates[last]) {
                return;
            }

            this.editingAssignment = {
                ...this.emptyAssignment(layout.startDates[first], layout.endDates[last]),
                participants: [{ employeeId, utilizationPercent: 100 }]
            };
            this.editorOpen = true;
            return;
        }

        // Verschieben oder Größe ändern: außerhalb des Kalenders losgelassen = Abbruch.
        if (!this.isInsideTimeline(event.clientX, event.clientY)) {
            this.cancelInteraction();
            return;
        }

        this.updateDragTarget(event);
        const final = this.interaction;
        const updated = (final.kind === 'moving' || final.kind === 'resizing-start' || final.kind === 'resizing-end')
            ? final.preview
            : null;

        this.resetInteraction();

        if (!updated) {
            return;
        }

        this.commitInteraction(updated);
    }

    @HostListener('document:pointercancel')
    onDocumentPointerCancel(): void {
        this.cancelInteraction();
    }

    /** Fenster verliert den Fokus (z. B. Alt+Tab mitten im Ziehen): sauber abbrechen. */
    @HostListener('window:blur')
    onWindowBlur(): void {
        this.cancelInteraction();
    }

    @HostListener('document:keydown.escape')
    onEscape(): void {
        if (this.interactionActive) {
            this.cancelInteraction();
        }
    }

    private cancelInteraction(): void {
        if (this.interaction.kind === 'idle') {
            return;
        }
        this.resetInteraction();
    }

    private resetInteraction(): void {
        const hadPreview = this.draggingAssignmentId !== null;
        this.interaction = { kind: 'idle' };
        this.activeCalendar = null;
        if (hadPreview) {
            this.invalidateBars();
        }
    }

    /** Öffentliche Variante für Templates/Tests: laufenden Vorgang abbrechen. */
    clearSelection(): void {
        this.cancelInteraction();
    }

    /** Geänderten Termin prüfen (Überlastung) und über die API speichern. */
    private commitInteraction(updated: PlanningAssignment): void {
        if (this.monitorMode) return;
        if (
            updated.startDate === updated.endDate &&
            updated.startTime >= updated.endTime
        ) {
            this.snackBar.open(
                'Das Ende muss zeitlich nach dem Beginn liegen.',
                'OK',
                { duration: 4000 }
            );
            return;
        }

        // Warnungen wie im Termin-Editor, Speichern trotzdem erlauben.
        const candidateAssignments = [
            ...this.assignments.filter(a =>
                a.id !== updated.id &&
                this.countsTowardsCapacity(a)
            ),
            ...(this.countsTowardsCapacity(updated) ? [updated] : [])
        ];

        const affectedEmployees = this.employees.filter(employee =>
            updated.participants.some(p => p.employeeId === employee.id)
        );

        const warnings = this.capacityService.getOverloadWarnings(
            affectedEmployees,
            candidateAssignments,
            updated.startDate,
            updated.endDate
        );

        if (warnings.length > 0) {
            const proceed = window.confirm(
                'Bei dieser Änderung entstehen Überlastungen:\n\n' +
                warnings.slice(0, 8).join('\n') +
                (warnings.length > 8 ? '\n…' : '') +
                '\n\nTrotzdem speichern?'
            );

            if (!proceed) {
                return;
            }
        }

        this.persist(updated, () => this.refreshAssignments());
    }

    isBarDragging(bar: ResourceTimelineBar): boolean {
        return this.draggingAssignmentId === bar.assignment.id;
    }
    getEmployeeSkills(employee: ResourceEmployee): ResourceKnowledge[] {
        return this.teamKnowledge.filter(skill =>
            employee.knowledgeIds.includes(skill.id)
        );
    }
    getSelectionColumn(): number {
        const i = this.interaction;
        if (i.kind !== 'selecting') {
            return 1;
        }
        return Math.min(i.startColumn, i.endColumn) + 1;
    }

    getSelectionSpan(): number {
        const i = this.interaction;
        if (i.kind !== 'selecting') {
            return 1;
        }
        return Math.abs(i.endColumn - i.startColumn) + 1;
    }

    openNewAssignment(): void {
        if (this.monitorMode) return;
        const today = this.toDateKey(new Date());

        this.editingAssignment = { ...this.emptyAssignment(today, today), teamId: this.selectedTeamId };
        this.editorOpen = true;
    }

    /** Leerer Termin mit der ersten Kategorie aus der Datenbank als Vorgabe. */
    private emptyAssignment(startDate: string, endDate: string): PlanningAssignment {
        const defaultCategory = this.editorCategories[0] ?? null;

        return {
            id: 0,
            title: '',
            startDate,
            endDate,
            startTime: '08:00',
            endTime: '16:00',
            categoryId: defaultCategory?.id ?? null,
            category: defaultCategory?.name ?? '',
            color: defaultCategory?.color ?? '#2563eb',
            participants: [],
            weekendDates: [],
            weekendDays: [],
            customerId: null,
            plantId: null,
            location: '',
            sendOutlookInvitation: false
        };
    }

    openAssignment(assignment: PlanningAssignment): void {
        if (this.monitorMode) return;
        if (this.isReadOnlyAssignment(assignment)) {
            this.explainReadOnly(assignment);
            return;
        }
        if (this.isExternalAssignment(assignment)) {
            this.openExternalAssignment(assignment);
            return;
        }
        this.editingAssignment = {
            ...assignment,
            participants: assignment.participants.map(p => ({ ...p })),
            weekendDates: [...assignment.weekendDates],
            weekendDays: (assignment.weekendDays ?? []).map(day => ({
                date: day.date,
                hoursByEmployee: { ...day.hoursByEmployee }
            }))
        };

        this.editorOpen = true;
    }

    saveAssignment(assignment: PlanningAssignment): void {
        if (this.monitorMode) return;
        this.persist(assignment, () => {
            this.refreshAssignments();
            this.closeEditor();
        });
    }

    deleteAssignment(id: number): void {
        if (this.monitorMode) return;
        if (this.saving) {
            return;
        }

        this.saving = true;

        // Gespeichert wird über die aktuelle Ansicht (Team oder Gesamtplanung), nicht
        // über die TeamId des Termins – ein Termin kann in mehreren Teamansichten erscheinen.
        const teamId = this.selectedTeamId;
        const deleteRequest = teamId !== null && teamId !== undefined
            ? this.api.deleteTeamAssignment(teamId, id)
            : this.api.deleteAssignment(id);
        deleteRequest.subscribe({
            next: () => {
                this.saving = false;
                this.snackBar.open('Termin gelöscht.', undefined, { duration: 2500 });
                this.refreshAssignments();
                this.closeEditor();
            },
            error: err => {
                this.saving = false;
                this.snackBar.open(
                    'Löschen fehlgeschlagen: ' + this.errorText(err),
                    'OK',
                    { duration: 6000 }
                );
            }
        });
    }

    closeEditor(): void {
        this.editorOpen = false;
        this.editingAssignment = null;
    }

    /** Speichert über die API; bei Erfolg wird onSuccess ausgeführt. */
    private persist(assignment: PlanningAssignment, onSuccess: () => void): void {
        if (this.monitorMode) return;
        if (this.saving) {
            return;
        }

        this.saving = true;

        const teamId = this.selectedTeamId;
        const saveRequest = teamId !== null && teamId !== undefined
            ? this.api.saveTeamAssignment(teamId, assignment)
            : this.api.saveAssignment(assignment);
        saveRequest.subscribe({
            next: saved => {
                this.saving = false;

                // Gespeicherten Stand sofort anzeigen (kein Zurückspringen bis
                // zum Neuladen); danach wird der Zeitraum frisch geladen.
                const exists = this.assignments.some(a => a.id === saved.id);
                this.assignments = exists
                    ? this.assignments.map(a => a.id === saved.id ? saved : a)
                    : [...this.assignments, saved];
                this.invalidateBars();

                this.snackBar.open('Termin gespeichert.', undefined, { duration: 2500 });
                onSuccess();
            },
            error: err => {
                this.saving = false;
                this.snackBar.open(
                    'Speichern fehlgeschlagen: ' + this.errorText(err),
                    'OK',
                    { duration: 6000 }
                );
            }
        });
    }

    private refreshAssignments(): void {
        this.loadAssignments(true);
    }

    private countsTowardsCapacity(
        assignment: PlanningAssignment
    ): boolean {
        const category = this.categories.find(
            c => c.id === assignment.categoryId
        );

        return !category?.excludeFromCapacity;
    }

    private get capacityAssignments(): PlanningAssignment[] {
        return this.assignments.filter(assignment =>
            this.countsTowardsCapacity(assignment)
        );
    }

    /** Lesbare Fehlermeldung aus einer HTTP-Antwort (BadRequest-Text oder ProblemDetails). */
    private errorText(err: any): string {
        const body = err?.error;

        if (typeof body === 'string' && body.trim()) {
            return body;
        }

        if (body && typeof body === 'object') {
            if (typeof body.message === 'string') {
                return body.message;
            }
            if (body.errors && typeof body.errors === 'object') {
                const messages = Object.values(body.errors as Record<string, string[]>)
                    .flat()
                    .filter(m => typeof m === 'string');
                if (messages.length) {
                    return messages.join(' ');
                }
            }
            if (typeof body.title === 'string') {
                return body.title;
            }
        }

        if (err?.status === 403) {
            return 'Keine Berechtigung.';
        }
        if (err?.status === 0) {
            return 'Server nicht erreichbar.';
        }

        return err?.message ?? 'Unbekannter Fehler.';
    }

}
